import { useEffect, useRef } from 'react';
import { useLazyQuery, useQuery } from 'data-fetching-client';
import { GET_ILS_LIST_METADATA, GET_ILS_LIST_SIZE, RESUME_LIST } from 'automation-ui-enrollment/queries/ILSListQueries';
import Metrics from 'automation-ui-enrollment/utils/Metrics';
const METADATA_POLL_INTERVAL_MS = 5000;

/**
 * Computes the list size for a given ILS list id. There is some nuance to how list sizes are calculated, but in general
 * the logic is as follows:
 *
 * 1. Fetch list metadata for the given ILS list. Poll every 5 seconds
 *   a. IF the list processing state is PAUSED, hit the resume endpoint to start processing the list again
 *   b. ELSE IF the list processing state is COMPLETE, stop polling
 *   c. ELSE continue polling
 * 2. Once polling has stopped (i.e., the list processing status is COMPLETE), fetch the list size via a separate endpoint
 */
const useIlsListSize = ({
  listId
}) => {
  const startTimeRef = useRef(performance.now());
  const [resumeList, resumeListResults] = useLazyQuery(RESUME_LIST, {
    nextFetchPolicy: 'network-only',
    fetchPolicy: 'network-only',
    variables: {
      listId
    }
  });
  const {
    loading: loadingListMetadata,
    data: listMetadata,
    error: listMetadataError,
    startPolling: startPollingListMetadata,
    stopPolling: stopPollingListMetadata
  } = useQuery(GET_ILS_LIST_METADATA, {
    nextFetchPolicy: 'network-only',
    fetchPolicy: 'network-only',
    variables: {
      listId
    }
  });
  const [getIlsListSize, {
    loading: loadingListCount,
    data: listCountData,
    error: listCountError
  }] = useLazyQuery(GET_ILS_LIST_SIZE, {
    nextFetchPolicy: 'network-only',
    fetchPolicy: 'network-only',
    variables: {
      listId
    }
  });
  const listProcessingStatus = listMetadata && listMetadata.ilsListMetadata.metadata.processingStatus;

  // Begin polling list processing status on mount
  useEffect(() => {
    startPollingListMetadata(METADATA_POLL_INTERVAL_MS);
    return () => {
      stopPollingListMetadata();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for changes in list processing status
  useEffect(() => {
    if (listProcessingStatus === 'PAUSED') {
      resumeList();
    } else if (listProcessingStatus === 'COMPLETE') {
      stopPollingListMetadata();
      getIlsListSize();
    }
  }, [listProcessingStatus, stopPollingListMetadata, resumeList, getIlsListSize]);

  // Tabulate metrics on how long this entire process took
  useEffect(() => {
    if (listCountData) {
      Metrics.timer('useIlsListSize-fetch-duration').update(performance.now() - startTimeRef.current);
      Metrics.histogram('useIlsListSize-list-size').update(listCountData.ilsListSize[listId]);
    }
  }, [listCountData, listId]);
  const isLoadingListData = loadingListCount || loadingListMetadata || !listProcessingStatus || listProcessingStatus !== 'COMPLETE' || !listCountData;
  return {
    loading: isLoadingListData,
    error: resumeListResults.error || listMetadataError || listCountError,
    listSize: listCountData && listCountData.ilsListSize[listId]
  };
};
export default useIlsListSize;